import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { cn } from "@/utils/cn";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const hTabKey = "hTab";
export const vTabKey = "vTab";

export type Tab = {
  value: string;
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
  count?: number;
  countColor?: string;
};

interface IProps {
  value?: string;
  tabConfig: Tab[];
  defaultValue?: string;
  styles?: {
    tabs?: string;
    tabsList?: string;
    tabsTrigger?: string;
    tabsContent?: string;
    selectedTab?: string;
  };
  isTabView?: boolean;
  handleTabChange?: (value: string) => void;
  /** Optional node pinned to the left of the tab strip (e.g. a page identity). */
  leading?: React.ReactNode;
  /** Optional node pinned to the right of the tab strip (e.g. context/actions). */
  trailing?: React.ReactNode;
  /**
   * Keep visited tabs mounted (hidden) instead of unmounting on switch. Tabs
   * mount lazily on first visit, then persist — so re-entering a heavy tab is
   * instant. Opt-in (heavier memory); use for expensive views like boards.
   */
  keepAlive?: boolean;
  /**
   * Render labels as written instead of the strip's default uppercase. Opt-in so
   * every existing tab bar keeps its current look.
   */
  preserveLabelCase?: boolean;
  /**
   * Mark the active tab with the underline alone, dropping the tinted pill
   * behind it. Opt-in so every existing tab bar keeps its current look.
   */
  underlineActive?: boolean;
  /**
   * Let the tab content sit straight on the page: the strip keeps its own
   * bordered bar, but there is no box around the content, so cards inside it
   * are not a border within a border. Opt-in.
   */
  bare?: boolean;
}

export const HorizontalTabs = ({
  value,
  styles,
  tabConfig,
  defaultValue,
  isTabView = true,
  handleTabChange,
  leading,
  trailing,
  keepAlive = false,
  preserveLabelCase = false,
  underlineActive = false,
  bare = false,
}: IProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(hTabKey);
  const isValidTab = (val?: string) =>
    tabConfig.some((tab) => tab.value === val);
  const [currentTab, setCurrentTab] = useState(
    tabFromUrl || value || defaultValue || tabConfig?.[0]?.value,
  );
  // keepAlive: remember which tabs have been opened so they stay mounted.
  const [mountedTabs, setMountedTabs] = useState<Set<string>>(
    () => new Set(currentTab ? [currentTab] : []),
  );
  useEffect(() => {
    if (keepAlive && currentTab) {
      setMountedTabs((prev) =>
        prev.has(currentTab) ? prev : new Set(prev).add(currentTab),
      );
    }
  }, [keepAlive, currentTab]);

  // Handle tab changes
  const handleTabSwitch = (newValue: string) => {
    setCurrentTab(newValue);

    // Update URL param without adding history entry. Built from the LIVE url
    // for the same reason as the sync effect below.
    setSearchParams(
      () => {
        const next = new URLSearchParams(window.location.search);
        next.delete(vTabKey);
        next.set(hTabKey, newValue);
        return next;
      },
      { replace: true },
    );

    // Trigger external callback
    handleTabChange?.(newValue);
  };

  useEffect(() => {
    // 1. Priority: If there's a valid tab in the URL, that wins.
    if (tabFromUrl && isValidTab(tabFromUrl)) {
      if (tabFromUrl !== currentTab) {
        setCurrentTab(tabFromUrl);
        handleTabChange?.(tabFromUrl);
      }
      return;
    }
    if (value && value !== currentTab && isValidTab(value)) {
      setCurrentTab(value);
      return;
    }
    if (!currentTab || !isValidTab(currentTab)) {
      const fallback = defaultValue || tabConfig?.[0]?.value;
      setCurrentTab(fallback);
    }
  }, [tabFromUrl, value, defaultValue, tabConfig]);

  /**
   * Keep the URL honest about what is on screen.
   *
   * `hTab` is one shared key across every tabbed page, but each page has its
   * own tab vocabulary — the employee form uses `personal-details` where the
   * employee profile uses `personal`. A param carried in from elsewhere (a
   * deep link, a back navigation, a stale bookmark) is silently fallen back on
   * above, which left the address bar naming a tab the user was not looking
   * at: copy the link and it reopens somewhere else again.
   *
   * `vTab` goes with it — a nested tab selection under a tab that is no longer
   * the one shown describes nothing.
   */
  useEffect(() => {
    if (!currentTab || !tabFromUrl || tabFromUrl === currentTab) return;
    setSearchParams(
      () => {
        // The LIVE url, not this render's snapshot — the convention the rest of
        // the codebase writes params by (`useTableParams`, `VerticalTabs`).
        // A debounced search landing between render and effect would otherwise
        // be reverted by a stale copy.
        const next = new URLSearchParams(window.location.search);
        next.set(hTabKey, currentTab);
        next.delete(vTabKey);
        return next;
      },
      { replace: true },
    );
  }, [currentTab, tabFromUrl, setSearchParams]);

  return (
    <Tabs
      value={currentTab}
      onValueChange={handleTabSwitch}
      className={cn(
        "w-full relative",
        styles?.tabs,
        isTabView && !bare && "bg-white shadow-none rounded-xs border",
      )}
    >
      <TabsList
        className={cn(
          "mb-4",
          styles?.tabsList,
          bare && "mb-4! rounded-sm! border border-border bg-white!",
          isTabView &&
            "bg-white shadow-none p-0 m-0 h-max border-b border-b-border rounded-t-xs! rounded-b-none overflow-x-auto w-full justify-start items-stretch scrollbar-thin",
        )}
      >
        {leading && (
          <div className="flex shrink-0 items-center gap-2 border-e border-border ps-3 pe-3">
            {leading}
          </div>
        )}
        {tabConfig.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            className={cn(
              styles?.tabsTrigger,
              currentTab === tab.value && styles?.selectedTab,
              isTabView &&
                "group data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:border-b-primary data-[state=active]:shadow-none hover:bg-primary/5 hover:text-primary rounded-none text-[10px]! md:text-xs! px-2 py-4 md:px-4 md:py-5 uppercase font-semibold relative border-b-transparent",
              "text-xs transition-colors duration-200",
              // Last, so it beats the base `uppercase` in the tailwind-merge
              // conflict — `styles.tabsTrigger` comes first and cannot.
              preserveLabelCase && "normal-case",
              underlineActive && "data-[state=active]:bg-transparent hover:bg-transparent",
            )}
          >
            <div className="flex items-center gap-2">
              {tab.label}
              {Boolean(tab.count) && (
                // A background is part of the default: without one the
                // `text-white` count was invisible on the light tab strip, so
                // every count silently disappeared unless a caller passed
                // `countColor`. That prop still overrides both colours.
                <span
                  className={cn(
                    "flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold",
                    "bg-primary-muted text-primary group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground",
                    tab.countColor,
                  )}
                >
                  {tab.count}
                </span>
              )}
            </div>
            <hr className="h-1 w-full bg-primary absolute bottom-0 start-0 hidden group-data-[state=active]:block transition-all" />
          </TabsTrigger>
        ))}
        {trailing && (
          <div className="ms-auto flex shrink-0 items-center gap-2 ps-3 pe-2">
            {trailing}
          </div>
        )}
      </TabsList>

      <div
        className={cn(
          "md:flex flex-col h-full",
          "max-md:max-h-[81dvh] ",
          "flex-1 p-4 overflow-auto rounded-b-xs scrollbar-thin",
          bare && "p-0 overflow-visible",
          styles?.tabsContent,
        )}
      >
        {tabConfig.map((tab) => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            forceMount={
              keepAlive && mountedTabs.has(tab.value) ? true : undefined
            }
            className={cn(
              "mt-0 data-[state=active]:flex flex-col flex-1 min-h-0",
              keepAlive && "data-[state=inactive]:hidden",
            )}
          >
            {tab.content}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
};
